import * as React from "react";
import { Alert, AlertTitle, Box, Card, Skeleton, Stack, Typography } from "@mui/material";
import { LockKeyIcon } from "@phosphor-icons/react/dist/ssr/LockKey";

import type { PlatformPrompt, PromptKey, PromptPayload } from "@/hooks/api/use-prompt-settings";
import {
	usePlatformPrompts,
	usePromptRunners,
	useRemovePromptOverride,
	useSavePrompt,
} from "@/hooks/api/use-prompt-settings";
import { useDialog } from "@/hooks/use-dialog";
import { statusCode } from "@/lib/admin-formatters";
import { useAuth } from "@/components/core/auth-provider";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { PromptEditor } from "./prompt-editor";

/** The order the prompts are laid out in: the agent is drafted before the list is dialled. */
const KEY_ORDER: PromptKey[] = ["AGENT_PROMPT_GENERATION", "LEAD_CLEANING", "POST_CALL_PROMPT_GENERATION"];

/**
 * The platform default first, then any account overrides of it.
 *
 * Sorted here rather than trusted from the API so an override always renders directly under the
 * prompt it replaces — an override sitting on its own, three cards away from the wording it
 * diverged from, is how the two stop being compared.
 */
function inReadingOrder(prompts: PlatformPrompt[]): PlatformPrompt[] {
	const rank = (prompt: PlatformPrompt): number => {
		const index = KEY_ORDER.indexOf(prompt.key);
		return index === -1 ? KEY_ORDER.length : index;
	};

	return prompts.toSorted((left, right) => {
		if (rank(left) !== rank(right)) return rank(left) - rank(right);
		if (left.accountId === right.accountId) return 0;
		if (left.accountId === null) return -1;
		if (right.accountId === null) return 1;
		return (left.accountName ?? "").localeCompare(right.accountName ?? "");
	});
}

/**
 * Where the prompts live and are edited.
 *
 * Platform-level and super-admin only, the same shape as the price book and the voice catalogue:
 * `platform_prompt` is a public-schema table, so what is typed here is what every account
 * generates from. That is the product owner's requirement in his own words — "I just set it once
 * as a super admin, and then all the accounts automatically pull that in. Saves me having to set
 * it up for every account" — and it is stated on the screen in one line rather than explained.
 *
 * The prompt is the page. There is no description of the generation pipeline, because a
 * super-admin opens this to change wording; what he needs to know about the contract is the two
 * lines above each box, and those come from the API so they cannot describe a stale one.
 *
 * An account override renders as its own card under the default it replaces, and can be dropped
 * from here. There is deliberately no control that creates one: per-account setup is the thing
 * this page exists to abolish, and a picker for the once-a-year exception would sit on the screen
 * every other day of the year.
 */
export function PromptSettings(): React.JSX.Element {
	const { user } = useAuth();
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	const { data, isLoading, isError, error } = usePlatformPrompts(isSuperAdmin);
	const { data: runners } = usePromptRunners(isSuperAdmin);

	const saveMutation = useSavePrompt();
	const removeMutation = useRemovePromptOverride();

	const {
		data: removeTarget,
		open: isRemoveOpen,
		handleOpen: openRemove,
		handleClose: closeRemove,
	} = useDialog<PlatformPrompt>();

	const prompts = React.useMemo(() => inReadingOrder(data ?? []), [data]);

	const handleSave = (id: string, payload: PromptPayload): Promise<PlatformPrompt> => {
		return saveMutation.mutateAsync({ id, data: payload });
	};

	// `mutate` rather than `mutateAsync`: a refusal is reported by the mutation's own toast, and
	// `DeleteModal` closes itself either way.
	const handleRemove = (): void => {
		if (!removeTarget) return;
		removeMutation.mutate(removeTarget.id);
	};

	const forbidden = !isSuperAdmin || statusCode(error) === 403;

	if (forbidden) {
		return (
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={3}>
					<Typography variant="h4">Prompt Settings</Typography>
					<Alert severity="warning" icon={<LockKeyIcon />}>
						<AlertTitle>This screen is for platform administrators</AlertTitle>
						<Typography variant="body2">
							These prompts are one cross-account set: what is written here is what every workspace generates from.
						</Typography>
					</Alert>
				</Stack>
			</Box>
		);
	}

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={3}>
					<Box>
						<Typography variant="h4">Prompt Settings</Typography>
						{/* The one thing about this page that looking at it will not tell you. */}
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Written once here — every account generates from these unless it has its own version.
						</Typography>
					</Box>

					{isError && statusCode(error) !== 403 ? (
						<Alert severity="error">The prompts could not be loaded. Refresh to try again.</Alert>
					) : null}

					{isLoading ? (
						<Card>
							<Stack spacing={1} sx={{ p: 2 }}>
								<Skeleton height={32} variant="rounded" />
								<Skeleton height={220} variant="rounded" />
							</Stack>
						</Card>
					) : null}

					{!isLoading && !isError && prompts.length === 0 ? (
						<Card>
							<Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
								No prompts are configured. Nothing is generated for any account until there are.
							</Typography>
						</Card>
					) : null}

					{prompts.map((prompt) => (
						<PromptEditor
							key={prompt.id}
							prompt={prompt}
							runners={runners ?? []}
							// The card being saved, not every card: two prompts are edited independently.
							isSaving={saveMutation.isPending && saveMutation.variables?.id === prompt.id}
							onSave={handleSave}
							onRemoveOverride={prompt.accountId ? (target) => openRemove(target) : undefined}
						/>
					))}
				</Stack>
			</Box>

			<DeleteModal
				open={isRemoveOpen}
				onClose={closeRemove}
				onDelete={handleRemove}
				title={`Put ${removeTarget?.accountName ?? "this account"} back on the platform default?`}
				description="Their own wording is deleted and they generate from the platform prompt from now on."
				deleteText="Use the default"
				warningText="Versions already saved for this account go with it."
			/>
		</React.Fragment>
	);
}
